import type { Cell, CellObject } from "write-excel-file/browser";
import {
  CallLog,
  CallExtraColumn,
  CALL_EXPORT_MAX_ROWS,
  ExportFetchOptions,
  fetchCallExtras,
} from "@/services/call.service";
import { callDurationSeconds } from "@/utils/call-duration";
import { formatDisconnectionReason } from "@/utils/disconnection-reasons";
import { orNA } from "@/utils/placeholder";

/** What the download is written as. */
export type CallExportFormat = "xlsx" | "csv";

// Excel serial dates carry no timezone, and the writer derives them from the
// UTC timestamp — shift by the local offset so a cell reads the same
// wall-clock time the Call History table shows.
const asLocalExcelDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
};

const header = (value: string) => ({ value, fontWeight: "bold" as const });

/** The label the table shows: the agent's name, or N/A once it was deleted (agent_id NULL). */
export const agentLabel = (call: Pick<CallLog, "agent_name">) => orNA(call.agent_name);

/**
 * The context a cell sees — the format being written, and a tally of the cells
 * Excel forced it to clip, so the caller can say so instead of leaving the
 * reader to find a truncated transcript on their own.
 */
type CellContext = {
  format: CallExportFormat;
  clipped: { count: number };
};

// Excel refuses a cell longer than 32,767 characters, and a clipped transcript
// that says nothing about being clipped reads as a complete one. CSV has no
// such limit, so a .csv export keeps the whole text.
const EXCEL_CELL_MAX = 32_767;
const TRUNCATION_NOTE = "… [truncated]";

const textCell = (text: string | null | undefined, ctx: CellContext): Cell => {
  if (!text) return { value: null };
  const clip = ctx.format === "xlsx" && text.length > EXCEL_CELL_MAX;
  if (clip) ctx.clipped.count += 1;
  return {
    value: clip
      ? text.slice(0, EXCEL_CELL_MAX - TRUNCATION_NOTE.length) + TRUNCATION_NOTE
      : text,
  };
};

/** True for the values that carry no information: null, {} and []. */
const isEmptyJson = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "object" && Object.keys(value as object).length === 0);

// An empty jsonb column is far and away the common case — two thirds of a real
// workspace's dynamic_variables are {} — and a column of literal "{}" reads as
// data. Blank is the honest rendering.
const jsonCell = (value: unknown, ctx: CellContext): Cell =>
  isEmptyJson(value) ? { value: null } : textCell(JSON.stringify(value), ctx);

const dateCell = (iso: string | null, withSeconds = false): Cell => ({
  value: asLocalExcelDate(iso),
  type: Date,
  format: withSeconds ? "yyyy-mm-dd hh:mm:ss" : "yyyy-mm-dd hh:mm",
});

const ROLE_LABELS: Record<string, string> = { agent: "Agent", user: "User", tool: "Tool" };

/** The turns held in transcript_json, or null when it carries no conversation. */
const conversationOf = (json: CallLog["transcript_json"]): Array<any> | null => {
  let parsed: unknown = json;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  const turns = (parsed as { conversation?: unknown } | null)?.conversation;
  return Array.isArray(turns) && turns.length > 0 ? turns : null;
};

/** The JSON conversation flattened the way the dialog renders it: one turn per line. */
const conversationText = (turns: Array<any>): string =>
  turns
    .map((turn) => {
      const text =
        turn?.content ||
        (turn?.role === "tool"
          ? turn.query || turn.description || `Executed ${turn.tool_name || "tool"}`
          : "");
      return text ? `${ROLE_LABELS[turn?.role] ?? turn?.role ?? "?"}: ${text}` : null;
    })
    .filter(Boolean)
    .join("\n");

/**
 * The transcript the Call Details dialog shows on its Transcript tab, by the
 * same rules (CallDetailsDialog.tsx:618-649): a manually fixed conversation
 * wins, then the raw turns the bot recorded, then the JSON. Exporting the raw
 * column alone would hand back the unfixed transcript to anyone who has used
 * "Fix Transcript". The dialog has one more fallback — the enhanced text —
 * which needs no fetch here: generate-perfect-transcript writes
 * enhanced_transcript and transcript_json together, so the JSON branch already
 * covers it, and Enhanced Transcript is a field of its own.
 */
const resolveTranscript = (call: CallLog): string | null => {
  const turns = conversationOf(call.transcript_json);
  if (turns && (call.transcript_fix_count || 0) > 0) return conversationText(turns);
  if (call.transcript && call.transcript.trim()) return call.transcript;
  return turns ? conversationText(turns) : null;
};

export interface CallExportField {
  id: string;
  label: string;
  width: number;
  /** Ticked when the dialog opens — these are the columns the table shows. */
  isDefault?: boolean;
  /** Columns the list query doesn't return; fetched only when this field is picked. */
  needs?: CallExtraColumn[];
  /** Carries a whole transcript or JSON blob — big enough to warn about in bulk. */
  isHeavy?: boolean;
  cell: (call: CallLog, ctx: CellContext) => Cell;
}

/** Every column an export can carry, in the order they land in the file. */
export const CALL_EXPORT_FIELDS: CallExportField[] = [
  { id: "from", label: "From", width: 18, isDefault: true, cell: (c) => ({ value: c.from_number }) },
  { id: "to", label: "To", width: 18, isDefault: true, cell: (c) => ({ value: c.to_number }) },
  {
    id: "agent",
    label: "Agent",
    width: 22,
    isDefault: true,
    cell: (c) => ({ value: agentLabel(c) }),
  },
  { id: "type", label: "Type", width: 10, isDefault: true, cell: (c) => ({ value: c.call_type }) },
  {
    id: "duration",
    label: "Duration (sec)",
    width: 14,
    isDefault: true,
    cell: (c) => ({ value: callDurationSeconds(c), type: Number, format: "0" }),
  },
  {
    id: "cost",
    label: "Cost ($)",
    width: 10,
    isDefault: true,
    // cost is stored in cents.
    cell: (c) => ({ value: (c.cost ?? 0) / 100, type: Number, format: "#,##0.00" }),
  },
  { id: "status", label: "Status", width: 12, isDefault: true, cell: (c) => ({ value: c.status }) },
  {
    id: "disconnection_reason",
    label: "Disconnection Reason",
    width: 22,
    isDefault: true,
    cell: (c) => ({
      value: c.disconnection_reason ? formatDisconnectionReason(c.disconnection_reason) : null,
    }),
  },
  {
    id: "date",
    label: "Date",
    width: 18,
    isDefault: true,
    cell: (c) => dateCell(c.created_at),
  },
  { id: "call_id", label: "Call ID", width: 38, isDefault: true, cell: (c) => ({ value: c.id }) },

  // Off by default — available for anyone who needs the raw record.
  { id: "started_at", label: "Started At", width: 20, cell: (c) => dateCell(c.started_at, true) },
  { id: "ended_at", label: "Ended At", width: 20, cell: (c) => dateCell(c.ended_at, true) },
  { id: "updated_at", label: "Updated At", width: 18, cell: (c) => dateCell(c.updated_at) },
  {
    id: "recording_url",
    label: "Recording URL",
    width: 44,
    needs: ["recording_url"],
    cell: (c) => ({ value: c.recording_url ?? null }),
  },
  {
    id: "summary",
    label: "Summary",
    width: 60,
    needs: ["summary"],
    cell: (c, ctx) => textCell(c.summary, ctx),
  },
  {
    id: "transcript",
    label: "Transcript",
    width: 80,
    needs: ["transcript", "transcript_json", "transcript_fix_count"],
    isHeavy: true,
    cell: (c, ctx) => textCell(resolveTranscript(c), ctx),
  },
  {
    id: "enhanced_transcript",
    label: "Enhanced Transcript",
    width: 80,
    needs: ["enhanced_transcript"],
    isHeavy: true,
    cell: (c, ctx) => textCell(c.enhanced_transcript, ctx),
  },
  {
    id: "analysis_data",
    label: "Analysis Data",
    width: 60,
    needs: ["analysis_data"],
    isHeavy: true,
    cell: (c, ctx) => jsonCell(c.analysis_data, ctx),
  },
  {
    id: "dynamic_variables",
    label: "Dynamic Variables",
    width: 40,
    needs: ["dynamic_variables"],
    cell: (c, ctx) => jsonCell(c.dynamic_variables, ctx),
  },
];

/** True if any picked field pulls a transcript or JSON blob for every row. */
export const hasHeavyExportField = (fieldIds: string[]): boolean =>
  CALL_EXPORT_FIELDS.some((f) => f.isHeavy && fieldIds.includes(f.id));

export const DEFAULT_CALL_EXPORT_FIELD_IDS = CALL_EXPORT_FIELDS.filter((f) => f.isDefault).map(
  (f) => f.id,
);

/** Cap on a transcript-carrying export, well under the plain-column cap. */
export const HEAVY_EXPORT_MAX_ROWS = 10000;

/**
 * How many rows this pick can safely carry. A transcript per row runs to tens
 * of KB, and the whole file is assembled in the tab's memory before the browser
 * ever sees it — 50,000 of those would take the page down long before it
 * finished, so a heavy pick gets the lower ceiling.
 */
export const exportRowLimit = (fieldIds: string[]): number =>
  hasHeavyExportField(fieldIds) ? HEAVY_EXPORT_MAX_ROWS : CALL_EXPORT_MAX_ROWS;

/** The same value the .xlsx cell would carry, as plain text for a CSV field. */
const cellText = (cell: Cell): string => {
  const { value, format } = cell as CellObject;
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    // Already shifted to local wall-clock for Excel's naive serials, so the
    // UTC form reads back the same time the sheet would show.
    return value
      .toISOString()
      .slice(0, !format || format.includes("ss") ? 19 : 16)
      .replace("T", " ");
  }
  return String(value);
};

// Spreadsheets execute a cell that opens with =, +, - or @, and transcripts and
// dynamic variables are text other people wrote (CWE-1236). Prefixing an
// apostrophe defuses it — but phone numbers legitimately open with "+" and
// negative numbers with "-", and mangling the From/To columns to defend against
// a formula would be a worse bug than the one being fixed, so anything that
// reads as a number or a phone number is left exactly as it is.
const NUMERIC_LOOKING = /^[+-]?[\d\s()./-]+$/;

const defuseFormula = (text: string): string =>
  /^[=@]/.test(text) || (/^[+-]/.test(text) && !NUMERIC_LOOKING.test(text)) ? `'${text}` : text;

// RFC 4180: a field holding a comma, quote or newline is quoted, and a quote
// inside it doubles.
const csvField = (value: string): string => {
  const text = defuseFormula(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const buildCsv = (rows: CallLog[], fields: CallExportField[], ctx: CellContext): string => {
  const lines = [fields.map((f) => csvField(f.label)).join(",")];
  for (const call of rows) {
    lines.push(fields.map((f) => csvField(cellText(f.cell(call, ctx)))).join(","));
  }
  // The BOM is what makes Excel open the file as UTF-8 rather than the
  // machine's local codepage, which mangles anything non-ASCII.
  return "\ufeff" + lines.join("\r\n");
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  // Revoked a tick later — Safari cancels the download if the URL dies first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** What a finished export is worth telling the user about. */
export interface CallExportResult {
  /** Cells Excel would not take whole — always 0 for a .csv. */
  clipped: number;
}

/**
 * Writes the given calls to an .xlsx or .csv file and hands it to the browser's
 * save dialog, with one column per picked field (in registry order). Fields the
 * list query doesn't cover are fetched here, only when they were picked.
 */
export async function exportCalls(
  calls: CallLog[],
  fieldIds: string[],
  options: { workspaceId: string; format: CallExportFormat; baseFileName: string } & ExportFetchOptions,
): Promise<CallExportResult> {
  const fields = CALL_EXPORT_FIELDS.filter((f) => fieldIds.includes(f.id));
  if (fields.length === 0) return { clipped: 0 };

  const extraColumns = Array.from(new Set(fields.flatMap((f) => f.needs ?? [])));
  let rows = calls;
  if (extraColumns.length > 0) {
    const extras = await fetchCallExtras(
      options.workspaceId,
      calls.map((c) => c.id),
      extraColumns,
      { signal: options.signal, onProgress: options.onProgress },
    );
    rows = calls.map((c) => ({ ...c, ...extras.get(c.id) }));
  }

  const ctx: CellContext = { format: options.format, clipped: { count: 0 } };
  const fileName = `${options.baseFileName}.${options.format}`;

  if (options.format === "csv") {
    const csv = buildCsv(rows, fields, ctx);
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), fileName);
    return { clipped: ctx.clipped.count };
  }

  // Loaded on demand — the xlsx writer only matters when someone exports one.
  const { default: writeXlsxFile } = await import("write-excel-file/browser");

  await writeXlsxFile(rows, {
    sheet: "Call History",
    stickyRowsCount: 1,
    columns: fields.map((f) => ({
      header: header(f.label),
      width: f.width,
      cell: (call: CallLog) => f.cell(call, ctx),
    })),
  }).toFile(fileName);

  return { clipped: ctx.clipped.count };
}
